
import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useAgentStore } from "@/store/agentStore";
import { useAnalytics } from "@/hooks/useAnalytics";
import "./ChatInput.css";

const SAMPLE_QUESTIONS = [
  "Show appointments of last week in pie chart by warehouse",
  "Show appointments of last week by status",
  "Show appointments of last week by carrier name",
];

export function ChatInput() {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const phase = useAgentStore((s) => s.current?.phase);
  const { submit } = useAnalytics();
  const messages = useAgentStore((s) => s.messages);
  const dataSource = useAgentStore((s) => s.dataSource);
  const uploadedFile = useAgentStore((s) => s.uploadedFile);
  const prefillQuery = useAgentStore((s) => s.prefillQuery);
  const clearPrefillQuery = useAgentStore((s) => s.clearPrefillQuery);
  const isRunning = phase === "running";
  const needsFile = dataSource === "excel" && !uploadedFile;
  const canSubmit = !!(input.trim() && !isRunning && !needsFile);

  // When a predefined report is selected, populate the input (keep pendingBaseSql alive for submission)
  useEffect(() => {
    if (prefillQuery) {
      setInput(prefillQuery);
      clearPrefillQuery();
      textareaRef.current?.focus();
    }
  }, [prefillQuery, clearPrefillQuery]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(
        textareaRef.current.scrollHeight,
        200
      ) + "px";
    }
  }, [input]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || isRunning) return;

    submit(trimmed);
    setInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="chat-input-wrapper">
      {/* Sample questions (when empty) */}
      {messages.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="sample-questions"
        >
          {SAMPLE_QUESTIONS.map((sample, i) => (
            <motion.button
              key={sample}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => setInput(sample)}
              disabled={isRunning}
              className="sample-btn"
            >
              {sample} →
            </motion.button>
          ))}
        </motion.div>
      )}

      {/* Input area */}
      <div className="input-row">
        {/* Textarea */}
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isRunning || needsFile}
            placeholder={needsFile ? "Upload an Excel file first…" : "Ask about your data… (⌘↵ to send)"}
            className="chat-textarea"
          />
        </div>

        {/* Send button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`send-btn${canSubmit ? " send-btn--active" : ""}`}
        >
          {isRunning ? <div className="send-btn-pulse" /> : "→"}
        </motion.button>
      </div>
    </div>
  );
}
